import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { formatPrice, SPORT_COLORS } from "@/lib/utils";

function sportIcon(sport, isLarge) {
  const colors = SPORT_COLORS[sport] || { bg: "#CCFF00", text: "#0A0A0A" };
  const size = isLarge ? 36 : 28;
  return L.divIcon({
    className: "",
    html: `<div style="
      width: ${size}px; height: ${size}px; border-radius: 0;
      background: ${colors.bg}; color: ${colors.text}; font-weight: 800;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 0 0 2px #0A0A0A, 0 0 20px rgba(0,0,0,0.5);
      transform: rotate(45deg); transition: all 0.2s;
    "><span style="transform: rotate(-45deg); font-size: ${isLarge ? 18 : 14}px;">●</span></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function MapHandler({ onBoundsChange, onMapReady }) {
  const map = useMap();
  const readyFired = useRef(false);

  useEffect(() => {
    if (!readyFired.current) {
      readyFired.current = true;
      if (onMapReady) onMapReady(map);
    }
  }, [map, onMapReady]);

  useEffect(() => {
    if (!map || !onBoundsChange) return;
    const handler = () => {
      onBoundsChange(map);
    };
    map.on("moveend", handler);
    return () => { map.off("moveend", handler); };
  }, [map, onBoundsChange]);

  return null;
}

function MapLegend() {
  const { t } = useTranslation();
  const sportsWithColor = Object.entries(SPORT_COLORS);

  return (
    <div style={{
      position: "absolute", bottom: 8, start: 8, zIndex: 2,
      background: "rgba(10,10,10,0.85)", backdropFilter: "blur(8px)",
      border: "1px solid rgba(255,255,255,0.08)",
      padding: "4px 8px", display: "flex", gap: 8, flexWrap: "wrap",
    }}>
      {sportsWithColor.map(([key, colors]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <div style={{
            width: 8, height: 8, background: colors.bg, transform: "rotate(45deg)",
            flexShrink: 0,
          }} />
          <span style={{
            fontSize: 7, letterSpacing: "0.15em", textTransform: "uppercase",
            color: "rgba(255,255,255,0.7)", fontWeight: 700,
          }}
            className="hidden sm:inline"
          >
            {t("home.sports." + key)}
          </span>
          <span style={{
            fontSize: 7, letterSpacing: "0.15em", textTransform: "uppercase",
            color: "rgba(255,255,255,0.7)", fontWeight: 700,
          }}
            className="sm:hidden"
          >
            {key.slice(0, 3)}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function MapView({ courts, center, zoom = 11, onBoundsChange, onMapReady, userLocation }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const fallbackCenter = center || (courts[0] ? [courts[0].lat, courts[0].lng] : [31.95, 35.93]);

  return (
    <div className={`${isDark ? "dark-map" : "light-map"} h-full w-full relative`} data-testid="map-view">
      <MapContainer
        center={fallbackCenter}
        zoom={zoom}
        scrollWheelZoom
        style={{ height: "100%", width: "100%", background: isDark ? "#0A0A0A" : "#f8f8f8", zIndex: 1 }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapHandler onBoundsChange={onBoundsChange} onMapReady={onMapReady} />
        {userLocation && (
          <Circle
            center={[userLocation.lat, userLocation.lng]}
            radius={20}
            pathOptions={{
              color: "#3B82F6",
              fillColor: "#3B82F6",
              fillOpacity: 0.4,
              weight: 2,
            }}
          />
        )}
        {courts.map((c) => (
          <Marker key={c.id} position={[c.lat, c.lng]} icon={sportIcon(c.sport)}>
            <Popup>
              <Link
                to={`/courts/${c.id}`}
                style={{ color: "#CCFF00", textDecoration: "none", fontWeight: 700 }}
              >
                {c.name}
              </Link>
              <div style={{ color: "#A1A1AA", fontSize: 12, marginTop: 4 }}>
                {formatPrice((c.slot_pricing || {})[String(c.default_slot_duration || c.slot_duration || 60)] ?? c.price_per_slot, c.currency)}/{c.default_slot_duration && c.default_slot_duration !== 60 ? t("court.per_min", { n: c.default_slot_duration }) : t("court.per_hour")} · {c.city}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <MapLegend />
    </div>
  );
}
